import React, { useEffect, useState, useMemo, useRef } from "react";
import { useDebounce } from "use-debounce";
import {
  Autocomplete,
  TextField,
  Typography,
  Stack,
  Box,
  InputAdornment,
  useTheme,
  alpha,
  Card,
  CardContent,
  Avatar,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import {
  Assignment,
  Terrain,
  Water,
  Agriculture,
  Warning,
  Notes,
  Numbers,
  Category,
  Texture,
  Landscape,
  Straighten,
  TrendingUp,
  Layers,
  DateRange,
  Person,
  ContentCopy,
  Grain,
  FilterHdr,
  Settings,
  MyLocation,
  Public,
  LocationOn,
  CheckCircle,
} from "@mui/icons-material";

import api from "../api";
import { fetchAllSondasByJob } from "../utils/sondaFetch";

import {
  FIZYOGRAFYA_OPTIONS,
  ANA_MATERYAL_OPTIONS,
  BTG_OPTIONS,
  EGIM_YUZDE_OPTIONS,
  TASLILIK_OPTIONS,
  CAKILLILIK_OPTIONS,
  KAYALILIK_OPTIONS,
  URUN_OPTIONS,
  SU_EROZYON_DERECESI_OPTIONS,
  TABII_DRENAJ_OPTIONS,
  ARAZI_KULLANIM_KATEGORI_OPTIONS,
} from "../constants/fieldConstants";
import { INITIAL_SONDA_DATA } from "../constants/sondaConstants";
import { BENZER_VERI_GETIR } from "../constants/featureFlags";

const ModernCardHeader = ({ title, icon, color }) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={2}
    sx={{ pb: 2, mb: 3, borderBottom: "1px solid rgba(0,0,0,0.06)" }}
  >
    <Avatar
      variant="rounded"
      sx={{ bgcolor: alpha(color, 0.1), color: color, width: 36, height: 36 }}
    >
      {React.cloneElement(icon, { fontSize: "small" })}
    </Avatar>

    <Typography
      variant="subtitle1"
      sx={{
        fontWeight: 800,
        color: "text.primary",
        letterSpacing: "0.5px",
        textTransform: "uppercase",
        fontSize: "0.80rem",
      }}
    >
      {title}
    </Typography>
  </Stack>
);

const ModernInputWrapper = ({ children, hasIcon }) => (
  <Box
    sx={{
      "& .MuiOutlinedInput-root": {
        borderRadius: "10px",
        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
        backgroundColor: "#fff",
        "& fieldset": { borderColor: "#e0e0e0" },
        "&:hover": {
          backgroundColor: "#fafafa",
          "& fieldset": { borderColor: "#bdbdbd" },
        },
        "&.Mui-focused": {
          backgroundColor: "#fff",
          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
          "& fieldset": { borderWidth: "1px", borderColor: "primary.main" },
        },
        "&.Mui-disabled": { backgroundColor: "#f5f5f5" },
      },
      "& .MuiInputLabel-root": {
        fontSize: "0.85rem",
        transform: hasIcon
          ? "translate(40px, 10px) scale(1)"
          : "translate(14px, 10px) scale(1)",
        "&.Mui-focused, &.MuiFormLabel-filled": {
          transform: "translate(14px, -9px) scale(0.75)",
        },
      },
      "& .MuiInputBase-input": { fontSize: "0.9rem", padding: "8.5px 14px" },
    }}
  >
    {children}
  </Box>
);

const RenderSelect = ({
  label,
  name,
  options,
  icon,
  formData,
  onAutoChange,
  disabled,
}) => (
  <ModernInputWrapper hasIcon={!!icon}>
    <Autocomplete
      fullWidth
      freeSolo
      disabled={disabled}
      options={options || []}
      getOptionLabel={(option) => {
        if (typeof option === "string") return option;
        return option?.label || option?.name || option?.toString() || "";
      }}
      isOptionEqualToValue={(option, value) => {
        if (!value || value === "") return false;
        const optVal =
          typeof option === "string" ? option : option?.label || option?.name;
        const curVal =
          typeof value === "string" ? value : value?.label || value?.name;
        return optVal === curVal;
      }}
      value={formData[name] || ""}
      onChange={(event, newValue) => {
        const selectedValue =
          typeof newValue === "object" && newValue !== null
            ? newValue.label || newValue.name || ""
            : newValue || "";
        onAutoChange(name, selectedValue);
      }}
      inputValue={formData[name] || ""}
      onInputChange={(event, newInputValue, reason) => {
        if (reason === "input" || reason === "clear") {
          onAutoChange(name, newInputValue || "");
        }
      }}
      filterOptions={(options, state) => {
        const inputValue = state.inputValue || "";
        if (!inputValue) return options;

        const searchVal = inputValue.toLocaleLowerCase("tr-TR").trim();

        return options.filter((item) => {
          if (!item) return false;
          const optionLabel =
            typeof item === "string"
              ? item
              : item.label || item.name || item.toString();

          const optionLower = optionLabel.toLocaleLowerCase("tr-TR");
          return optionLower.includes(searchVal);
        });
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant="outlined"
          disabled={disabled}
          InputProps={{
            ...params.InputProps,
            startAdornment: icon ? (
              <InputAdornment
                position="start"
                sx={{ color: "action.active", mr: 0.5 }}
              >
                {icon}
              </InputAdornment>
            ) : null,
          }}
        />
      )}
    />
  </ModernInputWrapper>
);

const RenderInput = ({
  label,
  name,
  formData,
  onChange,
  type = "text",
  icon,
  multiline = false,
  placeholder,
  disabled = false,
}) => (
  <ModernInputWrapper hasIcon={!!icon}>
    <TextField
      fullWidth
      label={label}
      name={name}
      type={type}
      value={formData[name] || ""}
      onChange={onChange}
      variant="outlined"
      placeholder={placeholder}
      multiline={multiline}
      rows={multiline ? 3 : 1}
      disabled={disabled}
      InputLabelProps={type === "date" ? { shrink: true } : {}}
      InputProps={{
        startAdornment:
          icon && !multiline ? (
            <InputAdornment
              position="start"
              sx={{ color: "action.active", mr: 0.5 }}
            >
              {icon}
            </InputAdornment>
          ) : null,
      }}
    />
  </ModernInputWrapper>
);

export default function SondaGeneralInfo({
  formData,
  setFormData,
  jobs,
  handleCountChange,
  similarProfiles = [],
  savedSondajlar = [],
  coordinators = [],
}) {
  const theme = useTheme();
  const [remoteSondas, setRemoteSondas] = useState([]);

  // NOT: "Benzer Sonda Sec" ile veri kopyalandiktan sonra eskiden tum diger
  // alanlar kitleniyordu (isLocked). Kullanici "acil" diye kaldirtti: artik
  // hicbir alan kilitlenmiyor, kopyalanan veriyi isterse degistirebilir.

  // --- "BENZER PROFIL" ALANINDAN OTOMATIK DOLDURMA -------------------------
  // Ornek/Istiksafi sondada "Benzer Profil" numarasi girildiginde (internet
  // varsa) o profildeki eğim, arazi kullanımı, taşlılık, ürün, çakıllılık,
  // kayalılık, su erozyonu, drenaj ve ilçe bilgileri DOĞRUDAN sonda formuna
  // yerleştirilir. Profil ve sonda alan adlari bazi yerlerde farkli oldugu
  // icin asagida eslestiriliyor.
  const [jobProfiles, setJobProfiles] = useState([]);
  const [profilDolduruldu, setProfilDolduruldu] = useState(false);
  const [debouncedBenzerProfil] = useDebounce(formData.benzer_profil, 600);
  // Ayni profil icin doldurma YALNIZCA BIR KEZ yapilir; boylece kullanici
  // doldurulan bir alani degistirdikten sonra efekt tekrar calisip degerini
  // ezmez ("hepsi yazilan degistirilebilir olsun").
  const sonDoldurulanRef = useRef("");

  useEffect(() => {
    const fetchJobProfiles = async () => {
      // Veri getirme kapaliysa profil listesi hic cekilmez (gereksiz istek yok).
      if (!BENZER_VERI_GETIR || !navigator.onLine || !formData.job_id) {
        setJobProfiles([]);
        return;
      }
      try {
        const res = await api.get(`/admin/job-profiles/${formData.job_id}`);
        if (res.data.success) setJobProfiles(res.data.data || []);
      } catch (err) {
        console.error("Çalışmaya ait profiller çekilemedi:", err);
      }
    };
    fetchJobProfiles();
  }, [formData.job_id]);

  useEffect(() => {
    if (!BENZER_VERI_GETIR) return;

    // Kontrol sondasi zaten "Benzer Sonda Sec" ile TUM veriyi kopyaliyor;
    // bu otomatik doldurma yalnizca Ornek/Istiksafi sondada calisir.
    if (formData.sonda_tipi === "Kontrol") {
      setProfilDolduruldu(false);
      return;
    }

    // Profil numarasi rakama indirgenerek karsilastirilir ("P12" ile "12"
    // ayni profildir; sahada iki bicim de kullanilabiliyor).
    const profilAnahtar = (v) => String(v ?? "").replace(/\D/g, "");
    const aranan = profilAnahtar(debouncedBenzerProfil);
    if (!aranan || jobProfiles.length === 0) {
      sonDoldurulanRef.current = "";
      setProfilDolduruldu(false);
      return;
    }

    const eslesen = jobProfiles.find(
      (p) => profilAnahtar(p.profil_no) === aranan,
    );
    if (!eslesen) {
      sonDoldurulanRef.current = "";
      setProfilDolduruldu(false);
      return;
    }

    // Bu profil icin zaten dolduruldu: kullanicinin sonraki duzeltmelerini ezme.
    const doldurmaAnahtari = `${formData.job_id}:${eslesen.id}`;
    if (sonDoldurulanRef.current === doldurmaAnahtari) {
      setProfilDolduruldu(true);
      return;
    }

    let veri = {};
    try {
      veri =
        typeof eslesen.full_data === "string"
          ? JSON.parse(eslesen.full_data)
          : eslesen.full_data || {};
    } catch (e) {
      return;
    }

    // Profil <-> Sonda alan adi eslestirmesi (isimler her zaman ayni degil).
    const guncelleme = {};
    if (veri.egim_yuzde) guncelleme.egim = veri.egim_yuzde;
    if (veri.arazi_kullanim_sinif)
      guncelleme.arazi_kullanim_sinif = veri.arazi_kullanim_sinif;
    if (veri.taslilik) guncelleme.taslilik = veri.taslilik;
    if (veri.urun) guncelleme.urun = veri.urun;
    if (veri.cakillilik) guncelleme.cakillilik = veri.cakillilik;
    if (veri.kayalilik) guncelleme.kayalilik = veri.kayalilik;
    if (veri.su_erozyon_derecesi)
      guncelleme.su_erozyonu = veri.su_erozyon_derecesi;
    if (veri.tabii_drenaj) guncelleme.drenaj = veri.tabii_drenaj;
    if (veri.ilce) guncelleme.ilce = veri.ilce;

    if (Object.keys(guncelleme).length === 0) return;

    sonDoldurulanRef.current = doldurmaAnahtari;
    setFormData((prev) => ({ ...prev, ...guncelleme }));
    setProfilDolduruldu(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedBenzerProfil, jobProfiles, formData.sonda_tipi]);

  // Bazalt ve Kireç Taşı seçeneklerini listeye otomatik ekleyen kod bloğu (Mükerrer eklemeleri engeller)
  const extendedAnaMateryalOptions = useMemo(() => {
    const baseOptions = Array.isArray(ANA_MATERYAL_OPTIONS)
      ? ANA_MATERYAL_OPTIONS
      : [];
    return Array.from(new Set([...baseOptions, "Bazalt", "Kireç Taşı"]));
  }, []);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      sonda_tipi: prev.sonda_tipi || "Örnek",
      drenaj: prev.drenaj || "İ(Drenaja gereksinim yok)",
      taslilik: prev.taslilik || "Taşsız",
      cakillilik: prev.cakillilik || "Yok",
      kayalilik: prev.kayalilik || "Yok",
      urun: prev.urun || "Nadas",
    }));
  }, [setFormData]);

  useEffect(() => {
    const fetchRemoteSondas = async () => {
      if (
        formData.sonda_tipi === "Kontrol" &&
        navigator.onLine &&
        formData.job_id
      ) {
        try {
          // Sunucu ucu sayfali: parametresiz cagrilirsa sadece ilk 50 kayit gelir
          // ve ajan kendi ornek sondasini listede bulamazdi.
          const filtered = await fetchAllSondasByJob(formData.job_id);
          setRemoteSondas(filtered);
        } catch (err) {
          console.error("Geçmiş sonda listesi çekilemedi:", err);
        }
      }
    };
    fetchRemoteSondas();
  }, [formData.sonda_tipi, formData.job_id]);

  const combinedSondaList = useMemo(() => {
    const combined = [...savedSondajlar, ...remoteSondas];
    const benzersiz = Array.from(
      new Map(combined.map((s) => [s.sonda_no, s])).values(),
    );
    // Bir kontrol sondasi yalnizca Ornek/Istiksafi bir sondaya baglanabilir.
    // Onceden diger kontrol sondalari da listede cikiyor ve yanlislikla
    // secilebiliyordu.
    return benzersiz.filter((s) => {
      let tip = s.sonda_tipi;
      if (!tip && s.full_data) {
        try {
          const d =
            typeof s.full_data === "string"
              ? JSON.parse(s.full_data)
              : s.full_data;
          tip = d?.sonda_tipi;
        } catch (e) {}
      }
      return tip !== "Kontrol";
    });
  }, [savedSondajlar, remoteSondas]);

  // STALE STATE HATASI BURADA ÇÖZÜLDÜ - formData yerine prev kullanıldı
  const handleChange = (e) => {
    let { name, value } = e.target;
    if (name === "sonda_no") {
      const digits = value.replace(/\D/g, "");
      value = digits ? "S" + digits : "";
    }
    if (name === "grup_no") {
      const digits = value.replace(/\D/g, "");
      value = digits ? "G" + digits : "";
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // STALE STATE HATASI BURADA ÇÖZÜLDÜ - formData yerine prev kullanıldı
  const handleAutoChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSondaTipiToggle = (tip) => {
    setFormData((prev) => ({
      ...prev,
      sonda_tipi: tip,
      ...(tip === "Kontrol" ? { cuval_no: "-", alma_sirasi: "-" } : {}),
    }));
  };

  // STALE STATE HATASI BURADA ÇÖZÜLDÜ - formData yerine prev kullanıldı
  const handleBenzerSondaSelect = (selection) => {
    if (!selection) {
      setFormData((prev) => ({ ...prev, benzer_sonda_no: "" }));
      return;
    }
    // Veri getirme KAPALI: yalnizca hangi ornek sondaya bagli oldugu kaydedilir
    // (izah cetveli kontrol sondalari bu bilgiye gore yerlestiriyor); forma
    // baska hicbir alan kopyalanmaz ve kullanicinin girdikleri ezilmez.
    if (!BENZER_VERI_GETIR) {
      setFormData((prev) => ({
        ...prev,
        sonda_tipi: "Kontrol",
        benzer_sonda_no: selection.sonda_no,
      }));
      return;
    }

    let sourceData =
      typeof selection.full_data === "string"
        ? JSON.parse(selection.full_data)
        : selection.full_data;

    setFormData((prev) => ({
      ...prev,
      ...sourceData,
      sonda_tipi: "Kontrol",
      sonda_no: prev.sonda_no,
      cuval_no: "-",
      alma_sirasi: "-",
      benzer_sonda_no: selection.sonda_no,
    }));
  };

  return (
    <Box
      sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 3 }}
    >
      <Card
        elevation={0}
        sx={{
          borderRadius: 4,
          border: "1px solid #eaecf0",
          background: "linear-gradient(to right, #fff, #f8f9fa)",
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={3}
            alignItems="center"
          >
            <Box sx={{ flexGrow: 1, width: "100%" }}>
              <ModernInputWrapper>
                <Autocomplete
                  options={jobs}
                  getOptionLabel={(o) => o.job_name || ""}
                  value={jobs.find((j) => j.id === formData.job_id) || null}
                  onChange={(e, v) => {
                    if (formData.job_id && formData.job_id !== v?.id) {
                      if (
                        !window.confirm(
                          "Çalışma değişirse form sıfırlanır. Onaylıyor musunuz?",
                        )
                      )
                        return;
                    }
                    // Calisma degisince form sifirlanir; ancak kullanicinin
                    // sectigi SONDA TIPI korunur. Aksi halde once "Kontrol"
                    // secip sonra calismayi secen kullanicida tip sessizce
                    // "Ornek"e donuyor ve "Benzer Sonda Sec" alani kayboluyordu.
                    setFormData((prev) => ({
                      ...INITIAL_SONDA_DATA,
                      sonda_tipi: prev.sonda_tipi || INITIAL_SONDA_DATA.sonda_tipi,
                      job_id: v?.id || null,
                      calismanin_adi: v?.job_name || "",
                      sehir: v?.province || "",
                      il: v?.province || "",
                    }));
                  }}
                  renderInput={(p) => (
                    <TextField
                      {...p}
                      label="Aktif Proje / Çalışma Seçimi"
                      variant="outlined"
                    />
                  )}
                />
              </ModernInputWrapper>
            </Box>

            <Stack
              direction="row"
              spacing={2}
              sx={{
                p: 1,
                bgcolor: "#fff",
                borderRadius: "12px",
                border: "1px solid #e0e0e0",
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.sonda_tipi === "Örnek"}
                    onChange={() => handleSondaTipiToggle("Örnek")}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2" fontWeight={700}>
                    Örnek Sonda
                  </Typography>
                }
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.sonda_tipi === "İstikşafi"}
                    onChange={() => handleSondaTipiToggle("İstikşafi")}
                    color="info"
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    color="info.main"
                  >
                    İstikşafi Sonda
                  </Typography>
                }
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.sonda_tipi === "Kontrol"}
                    onChange={() => handleSondaTipiToggle("Kontrol")}
                    color="secondary"
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    color="secondary"
                  >
                    Kontrol Sonda
                  </Typography>
                }
              />
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" },
          gap: 3,
        }}
      >
        <Card
          elevation={0}
          sx={{
            borderRadius: 4,
            border: "1px solid #eaecf0",
            overflow: "visible",
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <ModernCardHeader
              title="Sonda Kimlik Bilgileri"
              icon={<Assignment />}
              color={theme.palette.primary.main}
            />

            <Stack spacing={3}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "1fr 1fr",
                    md: "1fr 1fr 1fr",
                  },
                  gap: 2,
                }}
              >
                <RenderInput
                  label="Sonda No"
                  name="sonda_no"
                  formData={formData}
                  onChange={handleChange}
                  icon={<Numbers />}
                />
                <RenderInput
                  label="Grup No"
                  name="grup_no"
                  formData={formData}
                  onChange={handleChange}
                  icon={<Category />}
                />
                <Box>
                  <RenderInput
                    label="Benzer Profil"
                    name="benzer_profil"
                    formData={formData}
                    onChange={handleChange}
                    icon={<ContentCopy />}
                  />
                  {BENZER_VERI_GETIR &&
                    formData.sonda_tipi !== "Kontrol" &&
                    formData.benzer_profil &&
                    profilDolduruldu && (
                      <Stack
                        direction="row"
                        spacing={0.5}
                        alignItems="center"
                        sx={{ mt: 0.5, ml: 0.5 }}
                      >
                        <CheckCircle
                          sx={{ fontSize: 14, color: "success.main" }}
                        />
                        <Typography variant="caption" color="success.main">
                          Profil verileri otomatik dolduruldu
                        </Typography>
                      </Stack>
                    )}
                  {BENZER_VERI_GETIR &&
                    formData.sonda_tipi !== "Kontrol" &&
                    formData.benzer_profil &&
                    !profilDolduruldu &&
                    navigator.onLine && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 0.5, ml: 0.5, display: "block" }}
                      >
                        Bu numarada eşleşen profil bulunamadı.
                      </Typography>
                    )}
                </Box>

                {formData.sonda_tipi === "Kontrol" && (
                  <ModernInputWrapper hasIcon={true}>
                    <Autocomplete
                      options={combinedSondaList}
                      getOptionLabel={(o) => o.sonda_no || ""}
                      onChange={(e, v) => handleBenzerSondaSelect(v)}
                      renderInput={(p) => (
                        <TextField
                          {...p}
                          label="Benzer Sonda Seç"
                          placeholder="Sonda seç..."
                          variant="outlined"
                          InputProps={{
                            ...p.InputProps,
                            startAdornment: (
                              <InputAdornment position="start">
                                <ContentCopy
                                  fontSize="small"
                                  color="secondary"
                                />
                              </InputAdornment>
                            ),
                          }}
                        />
                      )}
                    />
                  </ModernInputWrapper>
                )}
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                  gap: 2,
                }}
              >
                <RenderInput
                  label="Tarih"
                  name="tarih"
                  type="date"
                  formData={formData}
                  onChange={handleChange}
                  icon={<DateRange />}
                />

                <RenderSelect
                  label="Örneği Alan"
                  name="ornegi_alan"
                  options={coordinators?.map((c) => c.full_name) || []}
                  icon={<Person />}
                  formData={formData}
                  onAutoChange={handleAutoChange}
                />

                <Box>
                  <ModernInputWrapper hasIcon={true}>
                    <TextField
                      label="Örnek Sayısı"
                      type="number"
                      fullWidth
                      value={formData.horizon_sayisi}
                      onChange={handleCountChange}
                      inputProps={{ min: 1, max: 20 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Layers sx={{ color: "warning.main" }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": { bgcolor: "#fff8e1" },
                      }}
                    />
                  </ModernInputWrapper>
                </Box>
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                  gap: 2,
                }}
              >
                <RenderInput
                  label="Koordinat X"
                  name="koordinat_x"
                  formData={formData}
                  onChange={handleChange}
                  icon={<MyLocation />}
                />

                <RenderInput
                  label="Koordinat Y"
                  name="koordinat_y"
                  formData={formData}
                  onChange={handleChange}
                  icon={<MyLocation />}
                />

                <RenderInput
                  label="Yükseklik (Z) m."
                  name="yukseklik"
                  type="number"
                  formData={formData}
                  onChange={handleChange}
                  icon={<Terrain />}
                />
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                  gap: 2,
                  mt: 2,
                }}
              >
                <RenderInput
                  label="İl"
                  name="il"
                  formData={formData}
                  onChange={handleChange}
                  icon={<Public />}
                />
                <RenderInput
                  label="İlçe"
                  name="ilce"
                  formData={formData}
                  onChange={handleChange}
                  icon={<LocationOn />}
                />
                <RenderInput
                  label="Mahalle / Köy"
                  name="mahalle"
                  formData={formData}
                  onChange={handleChange}
                  icon={<LocationOn />}
                />
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <Card
          elevation={0}
          sx={{
            borderRadius: 4,
            border: "1px solid #eaecf0",
            bgcolor: "#fcfcfd",
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <ModernCardHeader
              title="Lojistik"
              icon={<Settings />}
              color="#455a64"
            />

            <Stack spacing={3}>
              <RenderInput
                label="Çuval No"
                name="cuval_no"
                formData={formData}
                onChange={handleChange}
                icon={<Texture />}
              />

              <Box>
                <Typography
                  variant="caption"
                  fontWeight="bold"
                  color="text.secondary"
                  sx={{ mb: 1, display: "block" }}
                >
                  ÖRNEKLEME SIRASI
                </Typography>

                <RenderInput
                  label="Alma Sırası"
                  name="alma_sirasi"
                  type="number"
                  formData={formData}
                  onChange={handleChange}
                  icon={<Numbers />}
                />
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Box>

      <Card elevation={0} sx={{ borderRadius: 4, border: "1px solid #eaecf0" }}>
        <CardContent sx={{ p: 3 }}>
          <ModernCardHeader
            title="Toprak Karakteristiği"
            icon={<Terrain />}
            color="#2e7d32"
          />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
                md: "1fr 1fr 1fr 1fr",
              },
              gap: 2.5,
            }}
          >
            <RenderSelect
              label="Arazi Kullanımı"
              name="arazi_kullanim_sinif"
              options={ARAZI_KULLANIM_KATEGORI_OPTIONS}
              icon={<Agriculture fontSize="small" />}
              formData={formData}
              onAutoChange={handleAutoChange}
            />

            {/* BAZALT VE KİREÇ TAŞI DEĞERLERİ BURAYA GÖNDERİLDİ */}
            <RenderSelect
              label="Ana Materyal"
              name="ana_materyal"
              options={extendedAnaMateryalOptions}
              icon={<Terrain fontSize="small" />}
              formData={formData}
              onAutoChange={handleAutoChange}
            />

            <RenderSelect
              label="Büyük Toprak Grubu"
              name="btg"
              options={BTG_OPTIONS}
              icon={<Landscape fontSize="small" />}
              formData={formData}
              onAutoChange={handleAutoChange}
            />

            <RenderSelect
              label="Fizyografya"
              name="fizyografya"
              options={FIZYOGRAFYA_OPTIONS}
              icon={<FilterHdr fontSize="small" />}
              formData={formData}
              onAutoChange={handleAutoChange}
            />

            <RenderSelect
              label="Eğim (%)"
              name="egim"
              options={EGIM_YUZDE_OPTIONS}
              icon={<TrendingUp fontSize="small" />}
              formData={formData}
              onAutoChange={handleAutoChange}
            />

            <RenderInput
              label="Derinlik (cm)"
              name="derinlik"
              type="number"
              formData={formData}
              onChange={handleChange}
              icon={<Straighten fontSize="small" />}
            />

            <RenderSelect
              label="Drenaj"
              name="drenaj"
              options={TABII_DRENAJ_OPTIONS}
              icon={<Water fontSize="small" />}
              formData={formData}
              onAutoChange={handleAutoChange}
            />

            <RenderSelect
              label="Su Erozyonu"
              name="su_erozyonu"
              options={SU_EROZYON_DERECESI_OPTIONS}
              icon={<Warning fontSize="small" />}
              formData={formData}
              onAutoChange={handleAutoChange}
            />
            <RenderSelect
              label="Ürün"
              name="urun"
              options={URUN_OPTIONS}
              icon={<Agriculture fontSize="small" />}
              formData={formData}
              onAutoChange={handleAutoChange}
            />

            <RenderSelect
              label="Taşlılık"
              name="taslilik"
              options={TASLILIK_OPTIONS}
              icon={<Grain fontSize="small" />}
              formData={formData}
              onAutoChange={handleAutoChange}
            />

            <RenderSelect
              label="Çakıllık"
              name="cakillilik"
              options={CAKILLILIK_OPTIONS}
              icon={<Grain fontSize="small" />}
              formData={formData}
              onAutoChange={handleAutoChange}
            />

            <RenderSelect
              label="Kayalılık"
              name="kayalilik"
              options={KAYALILIK_OPTIONS}
              icon={<Terrain fontSize="small" />}
              formData={formData}
              onAutoChange={handleAutoChange}
            />
          </Box>
        </CardContent>
      </Card>

      <Card
        elevation={0}
        sx={{
          borderRadius: 4,
          border: "1px dashed #cfd8dc",
          bgcolor: "#fafafa",
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <ModernCardHeader
            title="Gözlemler & Notlar"
            icon={<Notes />}
            color="#607d8b"
          />

          <RenderInput
            label="Genel Notlar"
            name="gozlemler"
            formData={formData}
            onChange={handleChange}
            multiline
            placeholder="Gözlemlerinizi buraya yazın..."
          />
        </CardContent>
      </Card>
    </Box>
  );
}
